import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, UserPlus, Trash2 } from 'lucide-react'
import { dashboardAPI } from '../services/api'
import { useModalClose } from '../hooks/useModalClose'
import toast from 'react-hot-toast'
import axios from 'axios'

interface ShareDashboardModalProps {
  dashboardId: number
  dashboardName: string
  onClose: () => void
}

export default function ShareDashboardModal({ dashboardId, dashboardName, onClose }: ShareDashboardModalProps) {
  const [email, setEmail] = useState('')
  const [permissionType, setPermissionType] = useState<'view' | 'edit' | 'admin'>('view')
  const queryClient = useQueryClient()
  const { handleBackdropClick } = useModalClose(onClose)

  const { data: permissions = [] } = useQuery({
    queryKey: ['dashboard-permissions', dashboardId],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const { data } = await axios.get(
        `http://localhost:8000/api/dashboards/${dashboardId}/permissions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      return data
    },
  })

  const handleInvite = async () => {
    if (!email) {
      toast.error('Please enter an email address')
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.post(
        `http://localhost:8000/api/dashboards/${dashboardId}/permissions`,
        { email, permission_type: permissionType },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      queryClient.invalidateQueries({ queryKey: ['dashboard-permissions', dashboardId] })
      toast.success(`Invited ${email} with ${permissionType} access`)
      setEmail('')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send invitation')
    }
  }

  const handleRevoke = async (permissionId: number) => {
    if (!confirm('Are you sure you want to revoke this permission?')) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(
        `http://localhost:8000/api/permissions/${permissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      queryClient.invalidateQueries({ queryKey: ['dashboard-permissions', dashboardId] })
      toast.success('Permission revoked')
    } catch (error) {
      toast.error('Failed to revoke permission')
    }
  }

  const getPermissionBadgeClass = (type: string) => {
    switch (type) {
      case 'admin':
        return 'bg-purple-100 text-purple-800'
      case 'edit':
        return 'bg-primary-100 text-blue-800'
      case 'view':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[3000] flex items-center justify-center p-4" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Share Dashboard</h2>
            <p className="text-sm text-gray-600 mt-1">{dashboardName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X size={24} />
          </button>
        </div>

        {/* Invite Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Invite People</h3>
          
          <div className="flex gap-2 mb-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            />
            <select
              value={permissionType}
              onChange={(e) => setPermissionType(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="view">View Only</option>
              <option value="edit">Can Edit</option>
              <option value="admin">Admin</option>
            </select>
            <button
              onClick={handleInvite}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-2"
            >
              <UserPlus size={20} />
              Invite
            </button>
          </div>

          <div className="text-xs text-gray-600">
            <p className="mb-1"><strong>View Only:</strong> Can view the dashboard</p>
            <p className="mb-1"><strong>Can Edit:</strong> Can add, edit, and remove widgets</p>
            <p><strong>Admin:</strong> Full control including sharing and deleting</p>
          </div>
        </div>

        {/* Current Permissions */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            People with Access ({permissions.length})
          </h3>

          {permissions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No one else has access to this dashboard yet.</p>
              <p className="text-sm mt-1">Invite people using the form above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {permissions.map((permission: any) => (
                <div
                  key={permission.id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{permission.username}</div>
                    <div className="text-sm text-gray-600">{permission.email}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Granted {new Date(permission.granted_at).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPermissionBadgeClass(permission.permission_type)}`}>
                      {permission.permission_type === 'view' ? 'View Only' : 
                       permission.permission_type === 'edit' ? 'Can Edit' : 'Admin'}
                    </span>
                    <button
                      onClick={() => handleRevoke(permission.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Revoke access"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

